<template>
  <div class="ma-4 pt-4">
    <!-- navigation -->
    <top-navigation
      v-if="currentView !== 'bookingPaid'"
      :route-name="goBackRouteName"
      :title="$t('createBookingOverview.createNewBooking')"
      color="red"
      :button-text="$t('createBookingOverview.cancelBooking')"
      icon="fa-times"
    />

    <v-row class="justify-center no-gutters">
      <v-col class="col-12">
        <!-- create booking -->
        <create-booking
          v-show="currentView === 'booking'"
          ref="createBookingComponent"
          :basket-instance="basketInstance"
          :bookingStates="bookingStatesInstance"
          :selectedUserBookings.sync="selectedUserBookings"
          :display-mode="displayMode"
        />

        <!-- booking confirmation -->
        <booking-confirmation
          v-show="currentView === 'bookingConfirmation'"
          ref="bookingConfirmation"
          :basket="basketInstance"
          :bookingStates="bookingStatesInstance"
          :user.sync="selectedUserBookings"
        />

        <!-- after payment in cash desk was submitted: where to go next? -->
        <booking-paid
          v-if="currentView === 'bookingPaid'"
          ref="bookingPaid"
          :booking-id="bookingId"
        />
      </v-col>
    </v-row>

    <!-- navigation for back office's view -->
    <v-row v-if="displayMode === 'backOffice'">
      <!-- back button -->
      <v-col class="col-6">
        <v-btn
          color="primary"
          :disabled="currentView === 'booking'"
          large
          @click="navigation('previous')"
        >
          <span class="fal fa-chevron-left mr-2" />
          {{ $t('general.back') }}
        </v-btn>
      </v-col>

      <!-- next step -->
      <v-col class="col-6 text-right">
        <v-btn
          color="primary"
          :disabled="currentView === 'printTicket'"
          large
          @click="navigation('next')"
        >
          {{ nextText }}
          <span class="fal fa-chevron-right ml-2" />
        </v-btn>
      </v-col>
    </v-row>

    <!-- navigation for cash desk view -->
    <v-row v-else-if="displayMode === 'cashDesk'">
      <v-col v-if="currentView !== 'bookingPaid'" class="col-12 text-right">
        <v-btn x-large color="primary" @click="navigation('next')">
          <span class="fal fa-cash-register fa-2x mr-4" />
          {{ $t('createBookingOverview.customerPayed') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CreateBooking from '../../booking/create/CreateBooking'
import TopNavigation from '../../../components-shared/menus/TopNavigation'
import BookingConfirmation from '../../booking/create/BookingConfirmation'
import AdminCheckout from '../../../classes-internal/checkout/AdminCheckout'
import BookingStates from '../../../classes-shared/bookings/BookingStates'
import definitions from '../../../../definitions'
import BookingPaid from '../../booking/create/BookingPaid'
import AdminBasket from '../../../classes-shared/basket/AdminBasket'
import moment from 'moment'

export default {
  name: 'CreateBookingOverview',

  components: {
    BookingPaid,
    BookingConfirmation,
    TopNavigation,
    CreateBooking,
  },

  data() {
    return {
      // where all the booking lives
      basketInstance: new AdminBasket(),

      // current view: booking, mail, extraRide, print
      currentView: 'booking',

      // Type: UserBookings (extends User) => to which the booking belongs and the mail confirmation goes
      selectedUserBookings: null,

      bookingStatesInstance: new BookingStates(),

      // there are two distinct display modes for the component.
      // 1.) backOffice: Booking for the back office (includes a booking confirmation step)
      // 2.) cashDesk: Booking for the cash desk (includes a printing step)
      displayMode: 'backOffice',

      // after a ticket was created
      bookingId: null,
    }
  },

  computed: {
    nextText() {
      if (this.currentView === 'bookingConfirmation')
        return this.$t('createBookingOverview.confirmAndBook')
      else return this.$t('general.next')
    },

    goBackRouteName() {
      return this.displayMode === 'backOffice' ? 'bookingOverview' : 'cashDesk'
    },
  },

  beforeMount() {
    // set route if user cancels
    if (this.$route.params.displayMode)
      this.displayMode = this.$route.params.displayMode
  },

  async mounted() {
    // preselect product from cash desk
    if (this.$route.params.product)
      this.$refs.createBookingComponent.setSelectedProductOrEventInstance(
        this.$route.params.product
      )

    // set my own user (for back office cash desk)
    if (this.displayMode === 'cashDesk') {
      let userBookings = this.$store.getters
        .getAppUserInstance()
        .castToUserBookings()
      EventBus.$emit('bookingByCustomerSearch:addToUserList', userBookings)
      this.selectedUserBookings = userBookings
    }

    // passed date by router?
    if (this.$route.params.date) {
      let dateInstance = this.$route.params.date
      const dateString = moment(dateInstance.setHours(0, 0, 0, 0)).format(
        'YYYY-MM-DD'
      )
      this.$refs.createBookingComponent.setDate(dateString)
    }
  },

  methods: {
    async navigation(direction = 'next') {
      if (direction === 'next') {
        // check if at least one basket entry is available
        if (this.basketInstance.getBasketEntries().length) {
          if (this.currentView === 'booking') {
            if (this.displayMode === 'backOffice') {
              this.currentView = 'bookingConfirmation'
            } else if (this.displayMode === 'cashDesk') {
              // make cash desk booking and print tickets
              this.cashDeskCheckout()
            }
          } else if (this.currentView === 'bookingConfirmation') {
            // make admin booking
            this.adminCheckout(false)
          }
        } else {
          EventBus.$emit(
            'notify',
            this.$t('createBookingOverview.atLeasOneBasketEntryRequired'),
            'error'
          )
        }
      } else if (direction === 'previous') {
        // navigate back
        this.currentView = 'booking'
      }
    },

    // checkout made on cash desk
    async cashDeskCheckout() {
      if (this.displayMode !== 'cashDesk')
        throw new Error('Wrong display mode!')

      // set booking state to auto confirmed
      this.bookingStatesInstance.setBookingState(
        definitions.bookingState.manual_confirmed
      )

      // checkout
      await this.adminCheckout(true)
    },

    // create new admin booking
    async adminCheckout(paid = false) {
      if (
        this.currentView !== 'bookingConfirmation' ||
        (this.currentView === 'bookingConfirmation' &&
          this.$refs.bookingConfirmation.validate())
      ) {
        if (!this.bookingStatesInstance.getBookingState())
          throw new Error('No booking state given on admin checkout!')

        let uid = this.selectedUserBookings.getUid()
        if (!uid) throw new Error('No uid provided on admin checkout!')

        // get some variables from child component
        let note = this.$refs.bookingConfirmation.getNote()

        // perform admin checkout
        let adminCheckout = new AdminCheckout(this.basketInstance)
        this.bookingId = await adminCheckout.checkout(
          paid,
          this.bookingStatesInstance,
          note,
          uid
        )

        // send mail confirmation, if checkout was successful
        if (this.bookingId && this.displayMode !== 'cashDesk') {
          if (this.$refs.bookingConfirmation.getSendMail()) {
            let mailText = this.$refs.bookingConfirmation.getMailText()
            await adminCheckout.sendMailConfirmation(
              uid,
              this.$t('createBookingOverview.bookingConfirmation'),
              mailText
            )
          }

          if (this.displayMode === 'backOffice') {
            this.$router.push({
              name: 'bookingDetailView',
              params: { id: this.bookingId },
            })
          }
        }

        if (this.bookingId && this.displayMode === 'cashDesk')
          this.currentView = 'bookingPaid'
      }
    },
  },
}
</script>
